<template>
  <div class="session-wrapper justify-center justify-md-start" aria-live="polite">
    <div class="session-left"></div>
    <div class="session-right text-center">
      <div class="session-content">
        <img
          id="focus-target"
          role="banner"
          :src="logotype"
          tabindex="0"
          class="img-responsive mb-3"
          alt="Green Geeks logo"
          aria-label="Green Geeks logo"
          title="Green Geeks logo">
        <h1 class="mb-6 login-heading" tabindex="-1">
          Login to Your GreenGeeks Account
        </h1>

        <div v-if="errormsg">
          <p style="font-weight:bold;color:red;" role="alert">{{ errormsg }}</p>
        </div>

        <v-form data-vv-scope="login" class="mb-4">
          <v-container class="pa-0">
            <v-row>
              <v-col cols="12" class="pa-0">
                <v-text-field
                  label="E-mail"
                  outlined
                  v-model.trim="model.email"
                  data-vv-name="login.email"
                  data-vv-as="Email"
                  v-validate="rules.email"
                  :error-messages="errors.collect('login.email')">
                </v-text-field>
              </v-col>
              <v-col cols="12" class="pa-0">
                <v-text-field
                  label="Password"
                  outlined
                  v-model.trim="model.password"
                  :type="passwordFieldType"
                  data-vv-name="login.password"
                  data-vv-as="Password"
                  v-validate="rules.password"
                  :error-messages="errors.collect('login.password')">
                    <template v-slot:append>
                      <v-btn
                        icon
                        :ripple="false"
                        class="mt-0 mr-1"
                        @click="togglePasswordField"
                        :aria-label="passwordFieldType === 'password' ? 'Show password' : 'Hide password'"
                      >
                        <v-icon color="#C2C2C2">{{ passwordToggleIcon }}</v-icon>
                      </v-btn>
                    </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6" class="px-0">
                <v-checkbox
                  v-model="model.remember"
                  :ripple="false"
                  color="primary"
                  aria-label="Remember me"
                  class="mt-0"
                >
                  <template v-slot:label>
                    <label class="v-label" style="cursor:pointer;" aria-hidden="true">Remember me</label>
                  </template>
                </v-checkbox>
              </v-col>
              <v-col cols="12" sm="6" class="text-center text-sm-right px-0">
                <router-link :to="{ name: 'request_reset_password' }" class="pt-1">
                  Forgot password?
                </router-link>
              </v-col>
              <v-col cols="12" class="pa-0">
                <v-btn
                  type="submit"
                  block
                  tile
                  elevation="0"
                  dark
                  large
                  color="primary"
                  :loading="buttons.login"
                  @click.prevent="login(false)"
                >
                  Login
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </div>
    </div>
    <cookie-law theme="dark-lime">
      <template slot-scope="props">
        <p>
          This website uses cookies to ensure you get the best experience on our services.
          <a href="https://www.greengeeks.com/legal/privacy" target="_blank">More details</a>
          <v-btn color="primary" @click="props.accept" tile class="ml-md-3">Got it!</v-btn>
        </p>
      </template>
    </cookie-law>
    <gg-modal v-if="otp_required" :is-active="otp_required" @close="close2FAModal">
      <template v-slot:toolbar>
        Two factor authentication
      </template>
      <template v-slot:title>
        Please insert your 2FA code below
      </template>
      <template v-slot:body>
        <v-form data-vv-scope="otp" @submit.prevent="login(true)">
          <v-row>
            <v-col cols="12" class="pb-2">
              <v-text-field v-model="model.otp"
                            label="Token"
                            outlined
                            data-vv-name="otp.token"
                            data-vv-as="Token"
                            v-validate="rules.otp"
                            :error-messages="errors.collect('otp.token')"
              />
            </v-col>
            <v-col v-if="otp_error" cols="12">
              <v-alert type="error" dense>
                Your two-factor code is incorrect, please try again.
              </v-alert>
            </v-col>
          </v-row>
          <v-row class="my-0">
            <v-col class="py-0">
              <p><b>Note:</b> Using a recovery code will disable 2FA protection on your GreenGeeks account.</p>
            </v-col>
          </v-row>
        </v-form>
      </template>
      <template v-slot:actions>
        <v-btn color="primary" tile type="submit" :loading="buttons.otp" @click.prevent="login(true)">
          Submit
        </v-btn>
        <v-btn text @click="close2FAModal">
          Cancel
        </v-btn>
      </template>
    </gg-modal>
    <email2-f-a v-if="email2fa.dialog"
                :dialog="email2fa.dialog"
                @close="closeEmail2FAModal"
    />
    <!-- Advanced OTP     -->
    <gg-modal :is-active="advanced_otp.step_1.show" @close="closeAOTPStep1Modal">
      <template v-slot:toolbar>
        Unknown Device
      </template>
      <template v-slot:title>
        <h2 style="font-size: 20px;" tabindex="0">
          {{ advanced_otp.step_1.message }}
        </h2>
      </template>
      <template v-slot:body>
        <p>
          You are logging in from an unknown device. A security check is required to proceed. This check
          involves sending you a one-time password to a known communication method.
        </p>

        <p>Please select your desired communication method below.</p>

        <v-radio-group v-model="model.method">
          <v-radio v-for="(item, index) in advanced_otp.step_1.methods"
                   :key="index"
                   :value="item.value"
                   :aria-label="item.label"
          >
            <template v-slot:label>
              <div aria-hidden="true" tabindex="-1">{{ item.label }}</div>
            </template>
          </v-radio>
        </v-radio-group>
        Unable to verify? <a href="#" @click.prevent="openRecoverAccountModal">Start Recovery</a>
      </template>
      <template v-slot:actions>
        <v-btn color="primary"
               tile
               :loading="buttons.otp"
               :disabled="!valueSelected"
               @click="sendVerificationMethod"
        >
          Send Code
        </v-btn>
        <v-btn text @click="closeAOTPStep1Modal">
          Cancel
        </v-btn>
      </template>
    </gg-modal>
    <gg-modal :is-active="advanced_otp.step_2.show" @close="closeAOTPStep2Modal" role="dialog">
      <template v-slot:toolbar>
        Unknown Device
      </template>
      <template v-slot:title>
        <p class="hide-focus" id="otp-code-dialog-focus" tabindex="0">{{ advanced_otp.step_2.message }}</p>
      </template>
      <template v-slot:body>
        <div aria-live="polite" role="document">
          <v-alert v-if="advanced_otp.step_2.error_message" type="error" dense>
            {{ advanced_otp.step_2.error_message }}
          </v-alert>
          <v-text-field
            outlined
            clearable
            label="Code"
            id="otp-ud2-code"
            v-model="model.code"
            v-validate="rules.code"
            data-vv-name="code"
            data-vv-as="Validation code"
            :error-messages="errors.collect('code')"
            @keypress.enter="sendVerificationCode"
          />
          I didn’t receive the code. <a href="#" @click.prevent="showOtpResendCode">Resend my Code</a>
        </div>
      </template>
      <template v-slot:actions>
        <v-btn color="primary" tile :loading="buttons.otp" @click="sendVerificationCode">
          Verify Code
        </v-btn>
        <v-btn text @click="closeAOTPStep2Modal">
          Cancel
        </v-btn>
      </template>
    </gg-modal>
    <gg-modal :is-active="advanced_otp.step_3.show" @close="closeAOTPStep3Modal">
      <template v-slot:toolbar>
        Recover My Account
      </template>
      <template v-slot:body>
        <p>Unfortunately no other automated recovery methods are available. If you are not able to access any of the
          provided verification methods, you will need to work with our team directly to recover your account.</p>

        <a href="https://www.greengeeks.com/contact" target="_blank">
          Please contact our support team
        </a> to start the recovery process.
      </template>
      <template v-slot:actions>
        <v-btn color="primary" tile @click="closeAOTPStep3Modal">
          Close
        </v-btn>
      </template>
    </gg-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import CookieLaw from 'vue-cookie-law'
import Email2FA from 'Components/Auth/2FA/Email2FA'

export default {
  components: {
    CookieLaw,
    Email2FA,
  },
  data: () => ({
    model: {
      email: process.env.VUE_APP_DEV_USERNAME,
      password: process.env.VUE_APP_DEV_PASSWORD,
      otp: '',
      remember: false,
      action: undefined,
      method: undefined,
      code: undefined
    },
    passwordFieldType: 'password',
    email2fa: {
      dialog: false,
      email: '',
    },
    otp_required: false,
    errormsg: '',
    otp_error: false,
    buttons: {
      login: false,
      otp: false
    },
    rules: {
      email: {
        required: true,
        email: true
      },
      password: {
        required: true
      },
      otp: {
        required: true
      },
      code: {
        required: true
      }
    },
    advanced_otp: {
      step_1: {
        show: false,
        methods: [],
        message: ''
      },
      step_2: {
        show: false,
        message: '',
        error_message: ''
      },
      step_3: {
        show: false,
      },
    }
  }),
  methods: {
    ...mapActions(['attemptLogin', 'reportError', 'reportSuccess']),
    togglePasswordField () {
      this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password'
    },
    async login (otp = false) {
      try {
        // Clear error message
        this.errormsg = ''
        this.otp_error = false
        this.$notifications.clear()
        if (!await this.$validator.validateAll(otp ? 'otp' : 'login')) return
        this.buttons[otp ? 'otp' : 'login'] = true

        // Attempt Login
        const response = await this.attemptLogin({ ...this.model })

        if (response.require_otp) {
          // Clear OTP token in data
          this.otp = ''
          // Open OTP modal
          this.otp_required = true
        } else if (response.invalid_otp) {
          this.otp_error = true
          this.model.otp = ''
        } else if (response.require_email2fa) {
          this.otp_required = false
          this.email2fa.dialog = true
          this.email2fa.email = this.model.email
        } else if (response.require_advanced_otp) {
          if (response.select_method) {
            this.advanced_otp.step_1.show = true
            this.advanced_otp.step_1.methods = response.methods
            this.advanced_otp.step_1.message = response.resultmsg
          }

          if (response.code_sent) {
            this.advanced_otp.step_1.show = false
            this.model.action = undefined
            this.model.method = undefined
            this.advanced_otp.step_2.message = response.resultmsg
            // NVDA auto-read fix. Do NOT remove setTimeout.
            setTimeout(() => {
              this.advanced_otp.step_2.show = true
            }, 300)
          }

          if (response.retry) {
            this.model.code = undefined
            this.model.action = undefined
            this.advanced_otp.step_2.error_message = response.resultmsg
          }
        } else {
          const storedRedirect = localStorage.getItem('redirect')
          const route = storedRedirect ? { path: storedRedirect } : { name: 'dashboard' }
          await this.$router.push(route)
          localStorage.removeItem('redirect')
        }

        this.buttons[otp ? 'otp' : 'login'] = false
      } catch (e) {
        // AM-321 - Clear password after failed login attempt
        this.password = ''
        this.errormsg = e
        this.buttons[otp ? 'otp' : 'login'] = false
        this.reportError(e)
      }
    },
    async sendVerificationMethod () {
      this.model.action = 'send'
      await this.login(true)
    },
    async sendVerificationCode () {
      if (!await this.$validator.validateAll()) return
      this.advanced_otp.step_2.error_message = ''
      this.model.action = 'verify'
      await this.login(true)
    },
    showOtpResendCode () {
      this.advanced_otp.step_2.show = false
      this.advanced_otp.step_2.error_message = ''
      setTimeout(() => {
        this.advanced_otp.step_1.show = true
      }, 300)
    },
    openRecoverAccountModal () {
      this.closeAOTPStep1Modal()
      setTimeout(() => {
        this.advanced_otp.step_3.show = true
      }, 300)
    },
    close2FAModal () {
      this.otp_required = false
      this.otp_error = false
      this.model.password = ''
      this.$nextTick(() => {
        this.$validator.reset()
      })
    },
    closeEmail2FAModal () {
      this.email2fa.dialog = false
      this.model.password = ''
    },
    closeAOTPStep1Modal () {
      this.advanced_otp.step_1.show = false
      this.advanced_otp.step_1.methods = []
      this.advanced_otp.step_1.message = ''
      this.model.action = undefined
      this.model.method = undefined
    },
    closeAOTPStep2Modal () {
      this.advanced_otp.step_2.show = false
      this.advanced_otp.step_2.message = ''
      this.advanced_otp.step_2.error_message = ''
      this.model.action = undefined
      this.model.method = undefined
    },
    closeAOTPStep3Modal () {
      this.advanced_otp.step_3.show = false
    }
  },
  computed: {
    ...mapGetters(['isAuthenticated', 'brand']),
    logotype () {
      return this.$vuetify.theme.dark ? this.brand.appLogo : this.brand.darkLogo
    },
    valueSelected () {
      return this.model.method !== undefined
    },
    passwordToggleIcon () {
      return this.passwordFieldType === 'password' ? 'mdi-eye' : 'mdi-eye-off'
    }
  },
  mounted () {
    // A trick to fix buggy webkit password autofill: https://bugs.chromium.org/p/chromium/issues/detail?id=669724
    this.$nextTick(() => {
      setTimeout(() => {
        const webkitBuggyAutofill = document.querySelector('input[type="password"]:-webkit-autofill')
        if (!webkitBuggyAutofill) return
        const event = new Event('input', {
          bubbles: true,
          cancelable: true,
        })
        webkitBuggyAutofill.dispatchEvent(event)
      }, 500)
    })
  }
}
</script>

<style lang="scss">
.cookie-button {
  border-radius: 2px;
  background-color: #4caf50 !important;
  &:hover {
    background-color: #97D058 !important;
  }
}

.login-heading {
  font-size: 14px;
}

.hide-focus:focus-visible {
  outline: none !important;
}

.v-input__append-inner {
  margin: 8px 0 0 0 !important;
}
</style>
